import {
    LoginMutationApi,
    LoginMutationVariablesApi,
    useCopyWishlistToAnonymousWishlistMutationApi,
    useLoginMutationApi,
    useLogoutMutationApi,
} from 'graphql/generated';
import { removeSocialLoginDataCookie } from 'helpers/cookies/removeSocialLoginData';
import { isClient } from 'helpers/isServer';
import { dispatchBroadcastChannel } from 'hooks/useBroadcastChannel';
import { useRouter } from 'next/router';
import { usePersistStore } from 'store/usePersistStore';
import { useSessionStore } from 'store/useSessionStore';
import { OperationResult } from 'urql';
import { removeTokensFromCookies, setTokensToCookie } from 'utils/Auth/TokensFromCookies';

type LoginHandler = (
    variables: Omit<LoginMutationVariablesApi, 'productListsUuids'>,
    rewriteUrl?: string | boolean,
) => Promise<OperationResult<LoginMutationApi, LoginMutationVariablesApi>>;

export const useAuth = () => {
    const [, loginMutation] = useLoginMutationApi();
    const [, logoutMutation] = useLogoutMutationApi();
    const [, copyWishlistToAnonymousWishlistMutation] = useCopyWishlistToAnonymousWishlistMutationApi();

    const { updatePageLoadingState } = useSessionStore((s) => s);
    const router = useRouter();
    const { updateCartUuid, updateUserState, updateWishlistUuid, updateAuthLoadingState, wishlistUuid } =
        usePersistStore((s) => s);

    const login: LoginHandler = async (variables, rewriteUrl) => {
        const loginResult = await loginMutation({
            ...variables,
            productListsUuids: wishlistUuid === null ? [] : [wishlistUuid],
        });

        if (loginResult.data) {
            const accessToken = loginResult.data.Login.tokens.accessToken;
            const refreshToken = loginResult.data.Login.tokens.refreshToken;

            const uuid = loginResult.data.Login.uuid;
            const email = loginResult.data.Login.email;

            updateUserState({ userUuid: uuid });
            updateUserState({ userEmail: email });

            updateCartUuid(null);
            setTokensToCookie(accessToken, refreshToken);
            removeSocialLoginDataCookie();

            if (loginResult.data.Login.showCartMergeInfo === true) {
                updateAuthLoadingState('login-loading-with-cart-modifications');
            } else {
                updateAuthLoadingState('login-loading');
            }

            if (isClient && rewriteUrl !== false) {
                if (rewriteUrl && typeof rewriteUrl !== 'boolean') {
                    router.replace(rewriteUrl).then(() => router.reload());
                } else {
                    router.reload();
                }
            }
        }
        dispatchBroadcastChannel({ name: 'reloadPage' });

        return loginResult;
    };

    const logout: LogoutHandler = async () => {
        updateAuthLoadingState('logout-loading');
        let copiedWishlistUuid: string | null | undefined;

        if (wishlistUuid !== null) {
            try {
                const copyWishlistResult = await copyWishlistToAnonymousWishlistMutation({ wishlistUuid });

                if (!copyWishlistResult.error) {
                    copiedWishlistUuid = copyWishlistResult.data?.CopyWishlistToAnonymousWishlist?.uuid ?? null;
                }
            } catch {
                // Pass. Do not block logout on wishlist copy error
            }
        }
        const logoutResult = await logoutMutation({});

        if (logoutResult.data?.Logout) {
            if (copiedWishlistUuid !== undefined) {
                updateWishlistUuid(copiedWishlistUuid);
            }
            updateUserState({ userUuid: null });
            updateUserState({ userEmail: null });
            updatePageLoadingState({ isPageLoading: true, redirectPageType: 'homepage' });
            removeTokensFromCookies();
            updateCartUuid(null);

            if (isClient) {
                router.replace('/').then(() => router.reload());
            }
        } else {
            updateAuthLoadingState('not-loading');
        }
        dispatchBroadcastChannel({ name: 'reloadPage' });
    };

    return { login, logout };
};

type LogoutHandler = () => Promise<void>;
